/**
 * Turn an Auth0 failure into the setting that actually causes it. PURE.
 *
 * These errors are unhelpful on their own — several distinct misconfigurations produce
 * the same short string, and the console message names the symptom rather than the
 * cause. Mapping them here means a stuck login says which Auth0 field to look at
 * instead of leaving someone to guess between four plausible settings.
 */
export interface Diagnosis {
  summary: string;
  fix: string;
}

/**
 * An `?error=` handed back by Auth0 — a login it actively REFUSED, as opposed to an
 * exchange that broke. Separate from `diagnose` because the causes have nothing in
 * common: this one usually means our own Action said no.
 */
export function diagnoseRedirect(error: string, description: string): Diagnosis {
  const d = description || error;
  const lower = `${error} ${description}`.toLowerCase();

  // The post-login Action denies with three distinct messages, and they have three
  // distinct fixes. Lumping them sends people to check a healthy service while the real
  // cause is an unset field — so each is matched exactly as the Action writes it.
  // Source: cloud/access-service/auth0-action-post-login.js
  if (lower.includes("not configured")) {
    return {
      summary: "The Action is missing its own secrets.",
      fix: "Open the Action (Actions → Library → inject-mqtt-acl), click the key icon, " +
           "and add ACCESS_URL = https://access.norbot.ca and ACCESS_SECRET = the " +
           "AUTH0_ACTION_SHARED_SECRET value from fly. Then press Deploy. Action secrets " +
           "are PER-ACTION — setting them on one Action does nothing for the other, so " +
           "the Credentials Exchange Action needs the same pair.",
    };
  }
  if (lower.includes("unreachable")) {
    return {
      summary: "The Action could not reach the access service.",
      fix: "Check https://access.norbot.ca/health answers, and that the machine is not " +
           "stopped — a cold start can eat the Action's 5 s budget.",
    };
  }
  if (lower.includes("returned 401")) {
    return {
      summary: "The access service rejected the Action's credentials.",
      fix: "ACCESS_SECRET in the Action does not match AUTH0_ACTION_SHARED_SECRET on " +
           "fly. Note this is NOT the M2M client secret — see cloud/README.md, " +
           "\"Which is which\".",
    };
  }
  if (lower.includes("access service returned")) {
    return {
      summary: "The access service answered with an error.",
      fix: "Check `fly logs -a norbot-access`. A 503 means it could not read the grant " +
           "table — it fails closed rather than issuing a token granting nothing.",
    };
  }

  if (lower.includes("unauthorized_client") || lower.includes("no grants")) {
    return {
      summary: "Sign-in refused: this account has no access.",
      fix: "Grant it in the access table: norbot-access group member viewers '<your sub>'.",
    };
  }
  if (lower.includes("access_denied")) {
    return {
      summary: "Auth0 refused the sign-in.",
      fix: `Reported as: ${d}. This is usually a post-login Action denying — check the ` +
           `Auth0 log for this attempt (Monitoring → Logs), which names the Action.`,
    };
  }
  return { summary: `Auth0 returned "${error}".`, fix: d };
}

/**
 * Why a token could not be obtained for the broker. Distinct from a failed LOGIN: the
 * user is signed in and something stopped the app renewing silently.
 */
export function diagnoseToken(error: string): Diagnosis {
  const lower = error.toLowerCase();

  // The most likely misconfiguration, and the one with the least obvious symptom: the
  // app requests offline_access, and without rotation Auth0 issues no refresh token, so
  // the very first renewal fails.
  if (lower.includes("missing_refresh_token") || lower.includes("refresh token")) {
    return {
      summary: "No refresh token was issued, so the session cannot be renewed.",
      fix: "In Auth0, enable Refresh Token Rotation on the Norbot Fleet Web application " +
           "(Settings → Refresh Token Rotation), and make sure Allow Offline Access is " +
           "on for the https://mqtt.norbot.ca API. Login works without these; the first " +
           "renewal is what fails.",
    };
  }
  if (lower.includes("login_required") || lower.includes("consent_required")) {
    return {
      summary: "Auth0 wants a fresh interactive login.",
      fix: "The silent renewal was refused — usually blocked third-party cookies with " +
           "Refresh Token Rotation disabled. Sign in again, and enable rotation so it " +
           "does not recur.",
    };
  }
  if (lower.includes("timeout") || lower.includes("network") || lower.includes("fetch")) {
    return {
      summary: "Could not reach Auth0 to renew the session.",
      fix: "A network problem rather than a configuration one. It will retry on the " +
           "next reconnect.",
    };
  }
  return {
    summary: "Could not obtain a token for the broker.",
    fix: `Auth0 reported: ${error}. The browser console has the full error.`,
  };
}

/**
 * The broker refused a token we successfully obtained. Distinct from diagnoseToken:
 * Auth0 is fine, and the problem is what the broker checks.
 */
export function diagnoseBroker(detail: string): Diagnosis {
  const lower = detail.toLowerCase();

  // Historic cause, kept because it is invisible from the browser and easy to reintroduce:
  // EMQX string-compares verify_claims, and Auth0 sends `aud` as an ARRAY for interactive
  // logins (API identifier + /userinfo). Every service keeps working while every human
  // login fails, which makes it look like a per-user problem.
  if (lower.includes("bad user") || lower.includes("not authorized") ||
      lower.includes("not authoris")) {
    return {
      summary: "The broker refused the token.",
      fix: "The token was issued fine, so this is broker-side. Check `fly logs -a " +
           "norbot-mqtt` around the attempt. Known causes: verify_claims checking `aud` " +
           "(Auth0 sends an array for browser logins — it must not be checked), an " +
           "issuer mismatch including its trailing slash, or a JWKS the broker cannot " +
           "fetch.",
    };
  }
  return {
    summary: "The broker closed the connection.",
    fix: `Reported as: ${detail}`,
  };
}

export function diagnose(err: unknown, origin: string): Diagnosis {
  const raw = String((err as Error)?.message ?? err ?? "");
  const lower = raw.toLowerCase();

  if (lower.includes("callback url mismatch") || lower.includes("redirect_uri")) {
    return {
      summary: "Auth0 rejected the redirect target.",
      fix: `Add ${origin} to Allowed Callback URLs on the Norbot Fleet Web application.`,
    };
  }

  // The classic symptom of a Regular Web Application being used as a SPA: the token
  // endpoint demands client authentication that a browser cannot provide.
  if (lower.includes("unauthorized_client") || lower.includes("client_secret") ||
      lower.includes("client authentication")) {
    return {
      summary: "The token exchange was refused — this application may not be a SPA.",
      fix: "In Auth0, the application type must be Single Page Application. A Regular " +
           "Web Application requires a client secret, which a browser cannot hold, so " +
           "the PKCE exchange fails after an otherwise successful login.",
    };
  }

  // A CORS failure on /oauth/token surfaces as a network-ish error, because the browser
  // blocks it before Auth0 ever answers.
  if (lower.includes("cors") || lower.includes("failed to fetch") ||
      lower.includes("networkerror") || lower.includes("load failed")) {
    return {
      summary: "The browser blocked the call to Auth0 (CORS).",
      fix: `Add ${origin} to Allowed Web Origins on the application. This is a separate ` +
           `field from Allowed Callback URLs, and login appears to succeed without it — ` +
           `the exchange is what fails.`,
    };
  }

  if (lower.includes("grant type") || lower.includes("unsupported_grant")) {
    return {
      summary: "The application is not permitted to use this grant.",
      fix: "Enable the Authorization Code and Refresh Token grants (Application → " +
           "Advanced Settings → Grant Types).",
    };
  }

  if (lower.includes("consent") || lower.includes("access_denied")) {
    return {
      summary: "Auth0 declined the request.",
      fix: "Check the API is authorized for this application, and that the audience " +
           "matches an existing API Identifier exactly.",
    };
  }

  if (lower.includes("state") || lower.includes("invalid_grant")) {
    return {
      summary: "The login code was already used or has expired.",
      fix: "Usually a stale or reloaded callback URL. Sign in again from a clean page.",
    };
  }

  return {
    summary: "Login did not complete.",
    fix: "Open the browser console for the underlying error; it names the Auth0 " +
         "setting more precisely than this page can.",
  };
}
